/**
 * Canvas field mode switch.
 *
 * Three particle designs ship side by side so they can be compared on the real
 * pages. Resolution order: `?field=` query param, then localStorage, then the
 * default. `Shift+F` cycles and persists.
 */

import {
  DEFAULT_FIELD_MODE,
  FIELD_MODES,
  isFieldMode,
  type FieldMode,
} from '../../utils/visuals/field-modes.js'

export type { FieldMode }
export { FIELD_MODES }

const STORAGE_KEY = 'fbc:canvas-field-mode'
const QUERY_KEY = 'field'

let current: FieldMode = DEFAULT_FIELD_MODE
let overridden = false
let initialised = false

const listeners = new Set<(mode: FieldMode) => void>()

function readOverride(): FieldMode | null {
  if (typeof window === 'undefined') return null
  try {
    const fromQuery = new URLSearchParams(window.location.search).get(QUERY_KEY)
    if (isFieldMode(fromQuery)) return fromQuery
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (isFieldMode(stored) && stored !== 'ribbons') return stored
  } catch {
    // Private mode or blocked storage — fall back to the default.
  }
  return null
}

function init(): void {
  if (initialised) return
  initialised = true
  const override = readOverride()
  if (override) {
    current = override
    overridden = true
  }
}

export function getFieldMode(): FieldMode {
  init()
  return current
}

export function isFieldModeOverridden(): boolean {
  init()
  return overridden
}

export function setFieldMode(mode: FieldMode): void {
  init()
  current = mode
  overridden = true
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // Persistence is a convenience; the in-memory value still applies.
  }
  for (const listener of listeners) listener(mode)
}

export function cycleFieldMode(): FieldMode {
  const next = FIELD_MODES[(FIELD_MODES.indexOf(getFieldMode()) + 1) % FIELD_MODES.length]!
  setFieldMode(next)
  return next
}

export function subscribeFieldMode(listener: (mode: FieldMode) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
